'use client'

import { WalletSelector } from '@/components/WalletSelector'
import {
    assignClientToWallet,
    transferClients,
} from '@/lib/api/sales/wallets-api'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

/**
 * Atribuir ou trocar a carteira de um cliente.
 *
 * Estava duplicado na ficha do cliente e no atendimento — mesmo formulário, mesmo
 * `WalletSelector`, mesmos toasts, com uma diferença que não era intencional: só a
 * ficha aceitava devolver o cliente ao pool. Aqui as duas telas ganham o mesmo
 * comportamento, e `allowRemove` decide se a devolução aparece.
 *
 * Quem controla a carteira é o cliente, não a tela: depois de salvar, o chamador
 * recarrega o que precisa por `onSaved`.
 */

interface WalletAssignModalProps {
    isOpen: boolean
    /** Cliente que recebe (ou perde) a carteira. */
    clientId: number
    /** Carteira atual, para o campo abrir preenchido. */
    currentWalletId?: number | null
    /**
     * Nome do cliente/paciente, usado no texto. Sem ele, o texto fala em termos
     * genéricos — melhor que imprimir "undefined".
     */
    clientName?: string
    /**
     * Como a empresa chama quem é atendido ("cliente", "paciente"). A ficha já resolve
     * isso pelo tipo de negócio.
     */
    clientLabel?: string
    /**
     * Permite deixar em branco para devolver ao pool. No atendimento não faz sentido
     * (a ação de lá é "atribuir"), então nasce desligado.
     */
    allowRemove?: boolean
    onClose: () => void
    /** Chamado depois de salvar, para a tela recarregar o que mostra. */
    onSaved?: () => void
}

export function WalletAssignModal({
    isOpen,
    clientId,
    currentWalletId = null,
    clientName,
    clientLabel = 'cliente',
    allowRemove = false,
    onClose,
    onSaved,
}: WalletAssignModalProps) {
    const [walletId, setWalletId] = useState<number | null>(currentWalletId)
    const [moveDeals, setMoveDeals] = useState(true)
    const [saving, setSaving] = useState(false)

    // Reabrir o modal tem de mostrar a carteira atual, não a última escolha
    // abandonada — quem cancelou espera encontrar tudo como estava.
    useEffect(() => {
        if (isOpen) {
            setWalletId(currentWalletId)
            setMoveDeals(true)
        }
    }, [isOpen, currentWalletId])

    if (!isOpen) return null

    const isRemoving = allowRemove && walletId == null
    const title = currentWalletId ? 'Trocar carteira' : 'Atribuir carteira'

    const save = async () => {
        setSaving(true)
        try {
            // Sem carteira escolhida, o cliente volta ao pool — a transferência em
            // lote é a única rota que aceita destino nulo.
            const response = walletId
                ? await assignClientToWallet(walletId, clientId, moveDeals)
                : await transferClients([clientId], null)

            if (response.success) {
                toast.success(
                    isRemoving
                        ? `${clientName ?? 'Cliente'} devolvido ao pool`
                        : 'Carteira atualizada'
                )
                onSaved?.()
                onClose()
            } else {
                toast.error(response.error || 'Erro ao atualizar carteira')
            }
        } catch {
            toast.error('Erro ao atualizar carteira')
        } finally {
            setSaving(false)
        }
    }

    return (
        <dialog className="modal modal-open z-50">
            <div className="modal-box !max-w-md">
                <h3 className="text-lg font-bold mb-1">{title}</h3>
                <p className="text-sm text-base-content/60 mb-4">
                    Os membros da carteira escolhida passam a ver{' '}
                    {clientName ? (
                        <strong>{clientName}</strong>
                    ) : (
                        `este ${clientLabel}`
                    )}{' '}
                    e as conversas dele.
                    {allowRemove && ' Deixe em branco para devolvê-lo ao pool.'}
                </p>

                <WalletSelector
                    value={walletId}
                    onChange={id => setWalletId(id)}
                    label="Carteira"
                />

                {/* Devolver ao pool não tem funil de destino: a escolha não se aplica. */}
                {!isRemoving && (
                    <div className="form-control w-full pt-3">
                        <label
                            className="label cursor-pointer justify-start gap-3 py-2"
                            htmlFor="wallet-assign-move-deals"
                        >
                            <input
                                id="wallet-assign-move-deals"
                                type="checkbox"
                                className="checkbox checkbox-secondary checkbox-sm"
                                checked={moveDeals}
                                onChange={e => setMoveDeals(e.target.checked)}
                                disabled={saving}
                            />
                            <span className="label-text text-sm font-medium">
                                Levar as oportunidades abertas para o funil da nova dona
                            </span>
                        </label>
                        <p className="text-xs text-base-content/50 pl-9">
                            {moveDeals
                                ? 'As oportunidades em aberto mudam de funil e de responsável, na etapa de mesmo nome.'
                                : 'As oportunidades ficam onde estão. Quem as trabalha continua no funil dela e segue vendo a conversa.'}
                        </p>
                    </div>
                )}

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={save}
                        // Sem devolução permitida, salvar sem escolher não faz nada.
                        disabled={saving || (!allowRemove && walletId == null)}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : isRemoving ? (
                            'Devolver ao pool'
                        ) : (
                            'Salvar'
                        )}
                    </button>
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button onClick={onClose}>close</button>
            </form>
        </dialog>
    )
}
